import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import { useReactToPrint } from "react-to-print";
import { MdOutlineClose } from "react-icons/md";
import ApiService from "../../../ApiService";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const tableColumns = [
  { title: "REQ Id", field: "reqId" },
  { title: "Patient", field: "name" },
  { title: "Birth Date", field: "dob" },
  { title: "Location", field: "careLocation" },
  { title: "Clinician", field: "clinicianName" },
  { title: "Panel/Test", field: "panelName" },
  { title: "Collected Date-Time", field: "sampleTakenDate" }
];

const AddToShippingManifest = ({ carehubLocation, showModal, closeModal, manifestData }) => {
  const tableRef = useRef();
  const [currentDate, setCurrentDate] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  useEffect(() => {
    setDisableSubmit(true);
    setCurrentDate(new Date().toLocaleString());
  }, []);

  useEffect(() => {
    let isReadOnly = false;
    if (manifestData?.length) {
      isReadOnly = manifestData?.every(data => data?.kitStatus === "SAMPLE_SHIPMENT_MANIFEST_CREATED");
    }
    if (isReadOnly) {
      setTrackingNumber(manifestData[0]?.shipManifestNum);
    }
    setIsReadOnly(isReadOnly);
  }, [manifestData]);

  useEffect(() => {
    setDisableSubmit(trackingNumber === "");
  }, [trackingNumber]);

  const handleSubmit = async () => {
    setDisableSubmit(true);
    const req = {
      //comma separated kit ids
      kitIds: manifestData?.map(data => data.kitId).join(","),
      shipManifestId: trackingNumber,
      comment: "Shipping Manifest Created"
    };
    await ApiService.updateLabOrderWithShippingManifest(req);
    setSnackSeverity("success");
    setSnackText("Shipping Manifest Created Successfully. Please print the manifest and attach it to the package.");
    setSnackOpen(true);
    closeModal();
  };

  const handlePrint = useReactToPrint({
    content: () => {
      const table = tableRef.current;
      const tableClone = table.cloneNode(true);
      return tableClone;
    }
  });

  return (
    <Modal show={showModal} onHide={closeModal} size="xl">
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>
      <Modal.Header closeButton>
        <Modal.Title style={{ padding: "0px 0" }}>
          <div>
            <h3>Shipping Manifest</h3>
            <MdOutlineClose onClick={closeModal} style={{ cursor: "pointer", position: "absolute", right: "10px", top: "15px" }} />
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          style={{
            display: "flex",
            flexDirection: "row",
            flex: "1",
            columnGap: "60px"
          }}
        >
          <Form.Group controlId="trackingNumber" className="w-100">
            <Form.Label>Tracking Number:</Form.Label>
            <Form.Control
              type="text"
              readOnly={isReadOnly}
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value)}
              style={{ opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "auto" }}
            />
          </Form.Group>

          <Form.Group controlId="shippingProvider" className="w-100">
            <Form.Label>Shipping Provider:</Form.Label>
            <Form.Control
              as="select"
              readOnly={isReadOnly}
              style={{ opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "auto" }}
              disabled={isReadOnly}
            >
              <option value="FEDEX">FEDEX</option>
              <option value="UPS">UPS</option>
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="shippedDateTime" className="w-100" readOnly={isReadOnly}>
            <Form.Label>Shipped Date Time:</Form.Label>
            <Form.Control
              type="datetime-local"
              readOnly={isReadOnly}
              defaultValue={new Date().toISOString().slice(0, 16)}
              style={{ opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "auto" }}
            />
          </Form.Group>
        </Form>
        <hr />
        <div ref={tableRef}>
          <h5>Manifest Details</h5>
          <div>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold"
              }}
            >
              <strong
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "bold"
                }}
              >
                Partner:
              </strong>{" "}
              TBD Health - {carehubLocation}
            </p>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold"
              }}
            >
              <strong>Tracking Number:</strong> {trackingNumber}
            </p>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold"
              }}
            >
              <strong>Current Local Date:</strong> {currentDate}
            </p>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold"
              }}
            >
              <strong>Number of Lab Orders:</strong> {manifestData?.length}
            </p>
          </div>

          <Table striped>
            <thead>
              <tr>
                {tableColumns.map(column => (
                  <th key={column.field}>{column.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {manifestData?.map((data, index) => (
                <tr key={index}>
                  <td>{data.reqId}</td>
                  <td>{data.name}</td>
                  <td>{data.dob}</td>
                  <td>{data.careLocation}</td>
                  <td>{data.clinicianName}</td>
                  <td>{data.panelName}</td>
                  <td>{data.sampleTakenDate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {!isReadOnly && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={disableSubmit}
            style={{
              backgroundColor: disableSubmit ? "#B0B0B0" : "#0D6EFD",
              color: "white",
              cursor: disableSubmit ? "not-allowed" : "pointer"
            }}
          >
            Submit
          </Button>
        )}
        <Button variant="secondary" onClick={handlePrint}>
          Print Manifest
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToShippingManifest;
